/* eslint-disable no-unused-vars */
import { useState } from 'react';
import axios from 'axios';
import ApiEndpoints from '../../const/ApiEndpoints';
import useProject from '../Project/useProject';
import useUDFManager from '../UDFManager/useUDFManager';
import useDataIngestion from '../DM/useDataIngestion';
import useTaskManager from '../TaskManager/useTaskManager';
import utils from '../../utils/utils';
import usePipeline from '../Pipeline/usePipeline';

const BASE_URL = ApiEndpoints.MODULE_BASE_URL.DOC;
const totalCountHeader = 'x-total-count';

/**
 * @function useNewDocumentation
 * @description Custom hook for managing documentation-related data and actions.
 * @returns {object} Object containing various functions and state variables related to documentation.
 */
function useNewDocumentation() {
  const {
    fetchUdfs,
    udfs,
    setUdfs,
    fetchUDFTaskDetailsSplitAPI,
    fetchUdfDetails,
    fetchModuleTasksConsole,
  } = useUDFManager();
  const {
    project,
    fetchUserProjects,
    datasetList,
    fetchDatasetList,
    lockArtifact,
    unlockArtifact,
  } = useProject();
  const { publishDocumentToTask, getTaskDetails } = useTaskManager();
  const { cloneArtifacts } = useDataIngestion();
  const { fetchNormalOperationsTaskList } = usePipeline();

  // STATES
  const [docTableAndCharts, setDocTableAndCharts] = useState(null);
  const [allTemplate, setAllTemplate] = useState([]);
  const [activeDocSection, setActiveDocSection] = useState({});
  const [totalDocumentListCount, setTotalDocumentListCount] = useState(0);
  const [documentListPageCount, setDocumentListPageCount] = useState(0);

  /**
   * @function fetchRedirectURLForSharePoint
   * @description API call to get the redirect url
   * @param {number} documentId - document id whose share point need to be uploaded
   * @returns {Promise} Promise
   * @example fetchRedirectURLForSharePoint();
   */
  const fetchRedirectURLForSharePoint = (documentId) => {
    return axios
      .get(BASE_URL + ApiEndpoints.NEW_DOC.UPLOAD_SHARE_POINT, {
        params: {
          document_id: documentId,
        },
      })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function fetchDocumentList
   * @description API call to get the documents list
   * @param {number} projectId - data to get documents list
   * @returns {Promise} Promise
   * @example fetchDocumentList();
   */
  const fetchDocumentList = (projectId, field) => {
    return axios
      .get(BASE_URL + ApiEndpoints.NEW_DOC.DOC_LIST_HOME, {
        params: {
          project_id: projectId,
          ...(field ? { fields: field } : {}),
        },
      })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  const saveChart = (payload, type) => {
    const endpoints = {
      1: 'add-to-document',
      2: 'add-tag',
      3: 'add-document-tag',
    };

    const finalUrl = endpoints[type]
      ? `${BASE_URL + ApiEndpoints.NEW_DOC.SAVE_CHART}/${endpoints[type]}`
      : BASE_URL + ApiEndpoints.NEW_DOC.SAVE_CHART;

    return axios
      .post(finalUrl, payload)
      .then((res) => res)
      .catch((error) => {
        throw error;
      });
  };

  const saveTable = (payload, type) => {
    const endpoints = {
      1: 'add-to-document',
      2: 'add-tag',
      3: 'add-document-tag',
    };

    const finalUrl = endpoints[type]
      ? `${BASE_URL + ApiEndpoints.NEW_DOC.SAVE_TABLE}/${endpoints[type]}`
      : BASE_URL + ApiEndpoints.NEW_DOC.SAVE_TABLE;

    return axios
      .post(finalUrl, payload)
      .then((res) => res)
      .catch((error) => {
        throw error;
      });
  };

  const fetchDocDetail = (docId, field) => {
    return axios
      .get(BASE_URL + ApiEndpoints.NEW_DOC.DOC_DETAIL, {
        params: {
          document_id: docId,
          ...(field ? { fields: field } : {}),
        },
      })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };
  const deletePlaceholder = (placeholderId) => {
    return axios
      .post(`${BASE_URL + ApiEndpoints.NEW_DOC.DELETE_PLACEHOLDER}`, {
        id: placeholderId,
      })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  const fetchPlaceholderList = (docId) => {
    return axios
      .get(BASE_URL + ApiEndpoints.NEW_DOC.GET_PLACEHOLDERS, {
        params: {
          document_id: docId,
        },
      })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  const createPlaceholder = (docId, placeholderName) => {
    return axios
      .post(BASE_URL + ApiEndpoints.NEW_DOC.CREATE_PLACEHOLDER, {
        document_id: docId,
        placeholder_name: placeholderName,
      })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  const saveDocumentContent = (docId, data, fromTemplate) => {
    return axios
      .patch(BASE_URL + ApiEndpoints.NEW_DOC.DOC_SAVE, {
        document_id: docId,
        data,
        ...(fromTemplate ? { from_template: fromTemplate } : {}),
      })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };
  const updateDoc = (payload) => {
    return axios
      .post(BASE_URL + ApiEndpoints.NEW_DOC.DOC_UPDATE_DETAILS, payload)
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  const fetchDocumentDetails = (docId, field) => {
    return axios
      .get(BASE_URL + ApiEndpoints.NEW_DOC.DOC_VIEW, {
        params: {
          document_id: docId,
          ...(field ? { fields: field } : {}),
        },
      })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function fetchDocumentListWithPagination
   * @description API call to get the documents list
   * @param {number} projectId - data to get documents list
   * @returns {Promise} Promise
   * @example fetchDocumentListWithPagination();
   */
  const fetchDocumentListWithPagination = (
    projectId,
    pageIndex,
    pageSize,
    searchTerm,
    startDate,
    endDate,
    field,
    sortField,
    sortOrder
  ) => {
    return axios
      .get(BASE_URL + ApiEndpoints.NEW_DOC.DOC_LIST_HOME, {
        params: {
          project_id: projectId,
          page_index: pageIndex + 1,
          ...(pageSize ? { page_size: pageSize } : {}),
          ...(searchTerm ? { search_term: searchTerm } : {}),
          ...(startDate ? { start_date: startDate } : {}),
          ...(endDate ? { end_date: endDate } : {}),
          ...(field ? { field } : {}),
          ...(sortField ? { sort_field: sortField } : {}),
          ...(sortOrder ? { sort_order: sortOrder } : {}),
        },
      })
      .then(
        (res) => {
          if (res.data) {
            setTotalDocumentListCount(res?.headers?.[totalCountHeader]);
            setDocumentListPageCount(
              Math.ceil(res.headers[totalCountHeader] / pageSize)
            );
          } else {
            setTotalDocumentListCount(0);
            setDocumentListPageCount(0);
          }
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function createNewDoc
   * @description API call to create new document
   * @param {object} body - data to create new document
   * @returns {Promise} Promise
   * @example createNewDoc(body);
   */
  const createNewDoc = (body) => {
    return axios.post(BASE_URL + ApiEndpoints.NEW_DOC.DOC_CREATE, body).then(
      (res) => {
        return res?.data;
      },
      (error) => {
        throw error;
      }
    );
  };

  /**
   * @function fetchAllEditorOverview
   * @description API call to get all editor overview
   * @returns {Promise} AxiosPromise
   * @example fetchAllEditorOverview(fetchAllEditorOverview,successCallback,errorCallback);
   */
  const fetchAllEditorTemplate = (projectId) => {
    return axios
      .get(BASE_URL + ApiEndpoints.NEW_DOC.DOC_ALL_TEMPLATE, {
        params: {
          ...(projectId ? { project_id: projectId } : {}),
        },
      })
      .then(
        (res) => {
          setAllTemplate(res?.data);
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  const deleteDocument = (docID, fromTemplate) => {
    return axios
      .delete(`${BASE_URL + ApiEndpoints.NEW_DOC.DOC_DELETE}`, {
        data: {
          document_id: docID,
          ...(fromTemplate ? { from_template: fromTemplate } : {}),
        },
      })
      .then(
        (resp) => {
          return resp;
        },
        (error) => {
          throw error;
        }
      );
  };
  const fetchTaggedImages = (payload, hideLoaderOnRequest = false) => {
    return axios
      .get(BASE_URL + ApiEndpoints.NEW_DOC.GET_TAGGED_IMAGES, {
        params: {
          ...payload,
        },
        loader: !hideLoaderOnRequest,
      })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  const fetchTableById = (id) => {
    return axios
      .get(BASE_URL + ApiEndpoints.NEW_DOC.GET_TABLE, {
        params: {
          id,
        },
      })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };
  const fetchTaggedTables = (payload, hideLoaderOnRequest = false) => {
    return axios
      .get(BASE_URL + ApiEndpoints.NEW_DOC.GET_TABLE, {
        params: {
          ...payload,
        },
        loader: !hideLoaderOnRequest,
      })
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function saveTemplate
   * @description API call to save template
   * @param {object} body - data needed to save template
   * @returns {Promise} AxiosPromise
   * @example saveTemplate(body);
   */
  const saveTemplate = (body) => {
    return axios
      .post(BASE_URL + ApiEndpoints.NEW_DOC.DOC_SAVE_TEMPLATE, body)
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );
  };

  const deleteTemplate = (body) => {
    return axios
      .delete(BASE_URL + ApiEndpoints.NEW_DOC.DOC_SAVE_TEMPLATE, {
        data: { document_id: body },
      })
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );
  };
  const deleteTaggedResult = (params) => {
    return axios
      .delete(BASE_URL + ApiEndpoints.NEW_DOC.TAG_MANAGEMENT, {
        params,
      })
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );
  };

  const downloadDocument = (params, fileType, documentName) => {
    const fileExtension = fileType === 'pdf' ? 'pdf' : 'docx';

    return axios
      .get(BASE_URL + ApiEndpoints.NEW_DOC.DOWNLOAD_DOCUMENT, {
        params: {
          ...params,
        },
        responseType: 'blob', // We expect binary data (PDF, DOCX, etc.)
      })
      .then((res) => {
        const url = window.URL.createObjectURL(res.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `${documentName || 'document'}.${fileExtension}`
        );
        document.body.appendChild(link);
        link.click();
        return res;
      })
      .catch((error) => {
        throw error;
      });
  };

  const cloneDocument = (payload) => {
    return axios
      .post(BASE_URL + ApiEndpoints.NEW_DOC.CLONE_DOCUMENT, payload)
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );
  };

  const publishDocument = (payload) => {
    return axios
      .post(BASE_URL + ApiEndpoints.ME.PUBLISH_DOCUMENT_FROM_DOC, payload)
      .then(
        (res) => {
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function fetchAsyncResultNormalOperations
   * @description Fetches the asynchronous result of normal operations with the provided payload.
   * @param {object} payload - The data payload containing module, sub_module, and task_id.
   * @returns {Promise} - A Promise that resolves with the response data if successful, or rejects with an error.
   * @throws {Error} - Throws an error if the API call fails.
   */
  const fetchAsyncResultNormalOperations = (payload, resultType) => {
    const url = utils.getModuleTaskListUrl(payload.module, payload.sub_module);
    return axios
      .get(`${url}/${payload.task_id}/${resultType}/`)
      .then((res) => {
        return { ...(res?.data || {}), checkurl: url };
      })
      .catch((e) => {
        throw e;
      });
  };

  return {
    project,
    activeProject: project?.activeProject,
    datasetList,
    allTemplate,
    docTableAndCharts,
    activeDocSection,
    documentListPageCount,
    totalDocumentListCount,
    setDocGraphTableEmpty() {
      setDocTableAndCharts(null);
    },
    // functions
    setActiveDocSection,
    fetchUserProjects,
    fetchDatasetList,
    fetchDocumentList,

    createNewDoc,

    fetchAllEditorTemplate,
    fetchAsyncResultNormalOperations,
    fetchUDFTaskDetailsSplitAPI,
    fetchUdfDetails,
    fetchModuleTasksConsole,
    fetchNormalOperationsTaskList,

    // Console
    fetchUdfs,
    udfs,
    setUdfs,
    setDocumentListPageCount,
    setTotalDocumentListCount,

    lockArtifact,
    unlockArtifact,
    fetchDocumentListWithPagination,
    createPlaceholder,
    saveDocumentContent,
    fetchDocumentDetails,
    deleteDocument,
    fetchPlaceholderList,
    saveChart,
    saveTable,
    fetchDocDetail,
    fetchTableById,
    fetchTaggedImages,
    fetchTaggedTables,
    updateDoc,
    saveTemplate,
    deleteTemplate,
    deleteTaggedResult,
    downloadDocument,
    cloneDocument,
    cloneArtifacts,
    publishDocument,
    publishDocumentToTask,
    getTaskDetails,
    fetchRedirectURLForSharePoint,
    deletePlaceholder,
  };
}

export default useNewDocumentation;
